import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import cn from 'classnames';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { buildTemplatesUrl, formatDateToYYYYMMDD } from '@/shared/lib';
import {
  Button,
  Icon,
  Input,
  NavItem,
  NavList,
  Select,
  Textarea,
  toastCaller,
  VersionStatusBadge,
} from '@/shared/ui';
import { ValueType } from '@/shared/ui/Select/Select.types';

import {
  AnalyticsModel,
  analyticsModelModel,
  AnalyticsSection,
  exportAnalyticsModelToJson,
  TagGroup,
} from '@/entities/analyticsModel';
import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { templatesModel } from '@/entities/templates';
import { useContentLanguageOptions } from '@/entities/transcriptor';

import { analyticsModelMessages } from '../../config/i18n';
import {
  generateAnalyticsCodeFromName,
  getUniqueAnalyticsCode,
} from '../../lib/codeGenerator';
import {
  AnalyticsModelSettingsDraft,
  AnalyticsModelSettingsPopup,
} from '../AnalyticsModelSettingsPopup/AnalyticsModelSettingsPopup';
import { AnalyticsSectionEditor } from '../AnalyticsSectionEditor/AnalyticsSectionEditor';
import { TagGroupEditor } from '../TagGroupEditor/TagGroupEditor';

import styles from './AnalyticsModelForm.module.scss';

type AnalyticsModelFormProps = {
  className?: string;
  model?: AnalyticsModel;
  onBackToList?: () => void;
  onCloned?: (newModelId: string) => void;
  onDeleteComplete?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onSaveComplete?: (model: AnalyticsModel) => void;
};

type DraftGroup = TagGroup & { _draftId: string };
type DraftSection = AnalyticsSection & { _draftId: string };
type ActiveEditor =
  | {
      kind: 'group';
      id: string;
      value: DraftGroup;
      isNew: boolean;
    }
  | {
      kind: 'section';
      id: string;
      value: DraftSection;
      isNew: boolean;
    }
  | null;
type CodeEditorTarget =
  | {
      kind: 'model';
    }
  | {
      kind: 'group';
      id: string;
    }
  | {
      kind: 'section';
      id: string;
    };

type EditorDraft = {
  name: string;
  code: string;
  language: string;
  instructions: string;
  groups: DraftGroup[];
  sections: DraftSection[];
};
type WorkspaceBlock = 'details' | 'instructions' | 'sections' | 'groups';
type SidebarTarget = WorkspaceBlock | `group:${string}`;

const createDraftId = (prefix: string): string =>
  `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

const draftFromModel = (model: AnalyticsModel): EditorDraft => ({
  name: model.name,
  code: model.code,
  language: model.language,
  instructions: model.tags.instructions ?? '',
  groups: model.tags.groups.map((group) => ({
    ...group,
    type: group.type ?? 'ai',
    instructions: group.instructions ?? '',
    _draftId: createDraftId('group'),
  })),
  sections: (model.sections ?? []).map((section) => ({
    ...section,
    kind: 'text',
    instructions: section.instructions ?? '',
    _draftId: createDraftId('section'),
  })),
});

const newModelDraft = (): EditorDraft => ({
  name: '',
  code: '',
  language: 'en',
  instructions: '',
  groups: [],
  sections: [],
});

const resolveAnalyticsCode = (
  code: string,
  name: string,
  fallback: string,
): string => code.trim() || generateAnalyticsCodeFromName(name) || fallback;

const cloneDraftGroup = (group: DraftGroup): DraftGroup => ({
  ...group,
  tags: group.tags.map((tag) => ({ ...tag })),
});

const cloneDraftSection = (section: DraftSection): DraftSection => ({
  ...section,
});

const serializeDraft = (draft: EditorDraft): string =>
  JSON.stringify({
    name: draft.name.trim(),
    code: draft.code.trim(),
    language: draft.language,
    instructions: draft.instructions.trim(),
    groups: draft.groups.map((group) => ({
      code: group.code.trim(),
      name: group.name.trim(),
      type: group.type,
      instructions: (group.instructions ?? '').trim(),
      selection_mode: group.selection_mode,
      required: group.required,
      tags: group.tags.map((tag) => ({
        code: tag.code.trim(),
        name: tag.name.trim(),
        instructions: (tag.instructions ?? '').trim(),
        explain: tag.explain ?? false,
      })),
    })),
    sections: draft.sections.map((section) => ({
      code: section.code.trim(),
      name: section.name.trim(),
      kind: 'text',
      instructions: (section.instructions ?? '').trim(),
    })),
  });

const buildModelPayload = (
  draft: EditorDraft,
  original?: AnalyticsModel,
): AnalyticsModel => {
  const groupCodes = new Set<string>();
  const sectionCodes = new Set<string>();

  return {
    id: original?.id ?? '',
    series_id: original?.series_id ?? '',
    version: original?.version ?? 1,
    code: resolveAnalyticsCode(draft.code, draft.name, 'analytics_model'),
    language: draft.language,
    name: draft.name.trim(),
    status: original?.status ?? 'active',
    is_system: original?.is_system ?? false,
    organization_id: original?.organization_id,
    tags: {
      instructions: draft.instructions.trim() || null,
      groups: draft.groups.map((group, groupIndex) => {
        const groupCode = getUniqueAnalyticsCode(
          group.code,
          groupCodes,
          group.name || `group_${groupIndex + 1}`,
        );
        const tagCodes = new Set<string>();

        return {
          code: groupCode,
          name: group.name.trim(),
          type: group.type,
          instructions: group.instructions?.trim() || undefined,
          selection_mode: group.selection_mode,
          required: group.required,
          tags: group.tags.map((tag, tagIndex) => ({
            ...tag,
            code: getUniqueAnalyticsCode(
              tag.code,
              tagCodes,
              tag.name || `${groupCode}_tag_${tagIndex + 1}`,
            ),
            name: tag.name.trim(),
            instructions: tag.instructions?.trim() || undefined,
          })),
        };
      }),
    },
    sections: draft.sections.map((section, sectionIndex) => ({
      code: getUniqueAnalyticsCode(
        section.code,
        sectionCodes,
        section.name || `section_${sectionIndex + 1}`,
      ),
      name: section.name.trim(),
      kind: 'text',
      instructions: section.instructions?.trim() || undefined,
    })),
    created_at: original?.created_at ?? new Date().toISOString(),
  };
};

export const AnalyticsModelForm: FC<AnalyticsModelFormProps> = (props) => {
  const {
    className,
    model,
    onBackToList,
    onCloned,
    onDeleteComplete,
    onDirtyChange,
    onSaveComplete,
  } = props;

  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const currentOrganizationId = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const linkedSchemasMap = useAppSelector(
    templatesModel.selectors.selectEncounterSchemasByAnalyticsModelSeriesId,
  );

  const linkedSchemas = useMemo(
    () => (model?.series_id ? linkedSchemasMap.get(model.series_id) ?? [] : []),
    [linkedSchemasMap, model?.series_id],
  );

  const [draft, setDraft] = useState<EditorDraft>(() =>
    model ? draftFromModel(model) : newModelDraft(),
  );
  const [activeEditor, setActiveEditor] = useState<ActiveEditor>(null);
  const [codeEditorTarget, setCodeEditorTarget] =
    useState<CodeEditorTarget | null>(null);
  const [isModelCodeManual, setIsModelCodeManual] = useState(
    Boolean(model?.code),
  );
  const [activeSidebarTarget, setActiveSidebarTarget] =
    useState<SidebarTarget>('details');
  const seededFrom = useRef<string | null>(null);
  const dndSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const baselineSerialized = useMemo(
    () =>
      model
        ? serializeDraft(draftFromModel(model))
        : serializeDraft(newModelDraft()),
    [model],
  );

  // The editor re-reads its model on every open, so reseeding on identity alone
  // would discard whatever the author has typed since. Only a model with
  // different content is a different model to seed from.
  useEffect(() => {
    const signature = `${model?.id ?? 'new'}:${baselineSerialized}`;

    if (seededFrom.current === signature) {
      return;
    }

    seededFrom.current = signature;

    setDraft(model ? draftFromModel(model) : newModelDraft());
    setActiveEditor(null);
    setCodeEditorTarget(null);
    setIsModelCodeManual(Boolean(model?.code));
    setActiveSidebarTarget('details');
  }, [model, baselineSerialized]);

  const currentSerialized = useMemo(() => serializeDraft(draft), [draft]);
  const isDirty = baselineSerialized !== currentSerialized;
  const hasActiveEditor = activeEditor !== null;
  const codeEditorCode = useMemo(() => {
    if (!codeEditorTarget) {
      return '';
    }

    if (codeEditorTarget.kind === 'model') {
      return resolveAnalyticsCode(draft.code, draft.name, 'analytics_model');
    }

    if (codeEditorTarget.kind === 'group') {
      const groupIndex = draft.groups.findIndex(
        (group) => group._draftId === codeEditorTarget.id,
      );
      const fallbackCode = `group_${groupIndex === -1 ? 1 : groupIndex + 1}`;

      if (
        activeEditor?.kind === 'group' &&
        activeEditor.id === codeEditorTarget.id
      ) {
        return resolveAnalyticsCode(
          activeEditor.value.code,
          activeEditor.value.name,
          fallbackCode,
        );
      }

      const group = draft.groups.find(
        (item) => item._draftId === codeEditorTarget.id,
      );

      return group
        ? resolveAnalyticsCode(group.code, group.name, fallbackCode)
        : '';
    }

    const sectionIndex = draft.sections.findIndex(
      (section) => section._draftId === codeEditorTarget.id,
    );
    const fallbackCode = `section_${
      sectionIndex === -1 ? 1 : sectionIndex + 1
    }`;

    if (
      activeEditor?.kind === 'section' &&
      activeEditor.id === codeEditorTarget.id
    ) {
      return resolveAnalyticsCode(
        activeEditor.value.code,
        activeEditor.value.name,
        fallbackCode,
      );
    }

    const section = draft.sections.find(
      (item) => item._draftId === codeEditorTarget.id,
    );

    return section
      ? resolveAnalyticsCode(section.code, section.name, fallbackCode)
      : '';
  }, [
    activeEditor,
    codeEditorTarget,
    draft.code,
    draft.name,
    draft.groups,
    draft.sections,
  ]);

  useEffect(() => {
    onDirtyChange?.(isDirty);

    return () => {
      onDirtyChange?.(false);
    };
  }, [isDirty, onDirtyChange]);

  const isGroupEditing = useCallback(
    (groupId: string) =>
      activeEditor?.kind === 'group' && activeEditor.id === groupId,
    [activeEditor],
  );

  const isSectionEditing = useCallback(
    (sectionId: string) =>
      activeEditor?.kind === 'section' && activeEditor.id === sectionId,
    [activeEditor],
  );

  const { options: languageOptions } = useContentLanguageOptions(
    draft.language,
  );

  const handleModelNameChange = useCallback(
    (value: string) => {
      setDraft((current) => ({
        ...current,
        name: value,
        code: isModelCodeManual
          ? current.code
          : generateAnalyticsCodeFromName(value),
      }));
    },
    [isModelCodeManual],
  );

  const handleLanguageChange = useCallback(
    (value: React.ChangeEvent<Element> | ValueType) => {
      if (typeof value !== 'string') {
        return;
      }

      setDraft((current) => ({
        ...current,
        language: value,
      }));
    },
    [],
  );

  const handleSelectWorkspaceBlock = useCallback((block: WorkspaceBlock) => {
    setActiveSidebarTarget(block);

    requestAnimationFrame(() => {
      document
        .getElementById(`analytics-model-${block}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const handleSelectGroupNavItem = useCallback((groupId: string) => {
    setActiveSidebarTarget(`group:${groupId}`);

    requestAnimationFrame(() => {
      document
        .getElementById(`analytics-model-group-${groupId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const handleStartEditingGroup = useCallback(
    (group: DraftGroup) => {
      if (hasActiveEditor) {
        return;
      }

      setActiveEditor({
        kind: 'group',
        id: group._draftId,
        value: cloneDraftGroup(group),
        isNew: false,
      });
    },
    [hasActiveEditor],
  );

  const handleGroupDraftChange = useCallback((updated: TagGroup) => {
    setActiveEditor((current) => {
      if (!current || current.kind !== 'group') {
        return current;
      }

      return {
        ...current,
        value: {
          ...updated,
          _draftId: current.id,
          tags: updated.tags.map((tag) => ({ ...tag })),
        },
      };
    });
  }, []);

  const handleSaveGroupEdit = useCallback(() => {
    if (!activeEditor || activeEditor.kind !== 'group') {
      return;
    }

    setDraft((current) => ({
      ...current,
      groups: current.groups.map((group) =>
        group._draftId === activeEditor.id
          ? cloneDraftGroup(activeEditor.value)
          : group,
      ),
    }));
    setActiveEditor(null);
  }, [activeEditor]);

  const handleCancelGroupEdit = useCallback(() => {
    if (!activeEditor || activeEditor.kind !== 'group') {
      return;
    }

    if (activeEditor.isNew) {
      setDraft((current) => ({
        ...current,
        groups: current.groups.filter(
          (group) => group._draftId !== activeEditor.id,
        ),
      }));
    }

    setActiveEditor(null);
  }, [activeEditor]);

  const handleRemoveGroup = useCallback((groupId: string) => {
    setDraft((current) => ({
      ...current,
      groups: current.groups.filter((group) => group._draftId !== groupId),
    }));
    setActiveSidebarTarget((current) =>
      current === `group:${groupId}` ? 'groups' : current,
    );

    setActiveEditor((current) => {
      if (current?.kind === 'group' && current.id === groupId) {
        return null;
      }

      return current;
    });
  }, []);

  const handleAddGroup = useCallback(() => {
    if (hasActiveEditor) {
      return;
    }

    const newGroup: DraftGroup = {
      _draftId: createDraftId('group'),
      code: '',
      name: formatMessage(analyticsModelMessages.newGroup),
      type: 'ai',
      instructions: '',
      selection_mode: 'single',
      required: false,
      tags: [],
    };

    setDraft((current) => ({
      ...current,
      groups: [...current.groups, newGroup],
    }));
    setActiveEditor({
      kind: 'group',
      id: newGroup._draftId,
      value: cloneDraftGroup(newGroup),
      isNew: true,
    });
  }, [formatMessage, hasActiveEditor]);

  const handleStartEditingSection = useCallback(
    (section: DraftSection) => {
      if (hasActiveEditor) {
        return;
      }

      setActiveEditor({
        kind: 'section',
        id: section._draftId,
        value: cloneDraftSection(section),
        isNew: false,
      });
    },
    [hasActiveEditor],
  );

  const handleSectionDraftChange = useCallback((updated: AnalyticsSection) => {
    setActiveEditor((current) => {
      if (!current || current.kind !== 'section') {
        return current;
      }

      return {
        ...current,
        value: {
          ...updated,
          _draftId: current.id,
        },
      };
    });
  }, []);

  const handleSaveSectionEdit = useCallback(() => {
    if (!activeEditor || activeEditor.kind !== 'section') {
      return;
    }

    setDraft((current) => ({
      ...current,
      sections: current.sections.map((section) =>
        section._draftId === activeEditor.id
          ? cloneDraftSection(activeEditor.value)
          : section,
      ),
    }));
    setActiveEditor(null);
  }, [activeEditor]);

  const handleCancelSectionEdit = useCallback(() => {
    if (!activeEditor || activeEditor.kind !== 'section') {
      return;
    }

    if (activeEditor.isNew) {
      setDraft((current) => ({
        ...current,
        sections: current.sections.filter(
          (section) => section._draftId !== activeEditor.id,
        ),
      }));
    }

    setActiveEditor(null);
  }, [activeEditor]);

  const handleRemoveSection = useCallback((sectionId: string) => {
    setDraft((current) => ({
      ...current,
      sections: current.sections.filter(
        (section) => section._draftId !== sectionId,
      ),
    }));

    setActiveEditor((current) => {
      if (current?.kind === 'section' && current.id === sectionId) {
        return null;
      }

      return current;
    });
  }, []);

  const handleAddSection = useCallback(() => {
    if (hasActiveEditor) {
      return;
    }

    const newSection: DraftSection = {
      _draftId: createDraftId('section'),
      code: '',
      name: formatMessage(analyticsModelMessages.newSection),
      kind: 'text',
      instructions: '',
    };

    setDraft((current) => ({
      ...current,
      sections: [...current.sections, newSection],
    }));
    setActiveEditor({
      kind: 'section',
      id: newSection._draftId,
      value: cloneDraftSection(newSection),
      isNew: true,
    });
  }, [formatMessage, hasActiveEditor]);

  const handleStructureDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const activeId = String(active.id);
    const overId = String(over.id);

    setDraft((current) => {
      const activeSectionIndex = current.sections.findIndex(
        (section) => section._draftId === activeId,
      );
      const overSectionIndex = current.sections.findIndex(
        (section) => section._draftId === overId,
      );

      if (activeSectionIndex !== -1 && overSectionIndex !== -1) {
        return {
          ...current,
          sections: arrayMove(
            current.sections,
            activeSectionIndex,
            overSectionIndex,
          ),
        };
      }

      const activeGroupIndex = current.groups.findIndex(
        (group) => group._draftId === activeId,
      );
      const overGroupIndex = current.groups.findIndex(
        (group) => group._draftId === overId,
      );

      if (activeGroupIndex !== -1 && overGroupIndex !== -1) {
        return {
          ...current,
          groups: arrayMove(current.groups, activeGroupIndex, overGroupIndex),
        };
      }

      return current;
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (activeEditor) {
      return;
    }

    const payload = buildModelPayload(draft, model);

    try {
      const savedModel = model?.id
        ? await dispatch(
            analyticsModelModel.updateAnalyticsModel({
              id: model.id,
              data: {
                code: payload.code,
                language: payload.language,
                name: payload.name,
                tags: payload.tags,
                sections: payload.sections,
              },
            }),
          ).unwrap()
        : await dispatch(
            analyticsModelModel.createAnalyticsModel({
              code: payload.code,
              language: payload.language,
              name: payload.name,
              organization_id: payload.organization_id || currentOrganizationId,
              tags: payload.tags,
              sections: payload.sections,
            }),
          ).unwrap();

      toastCaller({
        type: 'success',
        message: formatMessage(analyticsModelMessages.saveSuccess),
      });
      onSaveComplete?.(savedModel);
    } catch (error) {
      toastCaller({
        type: 'error',
        message:
          error instanceof Error && error.message
            ? error.message
            : formatMessage(analyticsModelMessages.errorSave),
      });
    }
  }, [
    activeEditor,
    currentOrganizationId,
    dispatch,
    draft,
    formatMessage,
    model,
    onSaveComplete,
  ]);

  const handleSettingsSave = useCallback(
    (data: AnalyticsModelSettingsDraft) => {
      if (!codeEditorTarget) {
        return;
      }

      if (codeEditorTarget.kind === 'model') {
        setDraft((current) => ({
          ...current,
          code: data.code,
        }));
        setIsModelCodeManual(true);
        setCodeEditorTarget(null);
        return;
      }

      if (codeEditorTarget.kind === 'group') {
        if (
          activeEditor?.kind === 'group' &&
          activeEditor.id === codeEditorTarget.id
        ) {
          setActiveEditor((current) =>
            current?.kind === 'group' && current.id === codeEditorTarget.id
              ? {
                  ...current,
                  value: {
                    ...current.value,
                    code: data.code,
                  },
                }
              : current,
          );
        } else {
          setDraft((current) => ({
            ...current,
            groups: current.groups.map((group) =>
              group._draftId === codeEditorTarget.id
                ? {
                    ...group,
                    code: data.code,
                  }
                : group,
            ),
          }));
        }
        setCodeEditorTarget(null);
        return;
      }

      if (
        activeEditor?.kind === 'section' &&
        activeEditor.id === codeEditorTarget.id
      ) {
        setActiveEditor((current) =>
          current?.kind === 'section' && current.id === codeEditorTarget.id
            ? {
                ...current,
                value: {
                  ...current.value,
                  code: data.code,
                },
              }
            : current,
        );
      } else {
        setDraft((current) => ({
          ...current,
          sections: current.sections.map((section) =>
            section._draftId === codeEditorTarget.id
              ? {
                  ...section,
                  code: data.code,
                }
              : section,
          ),
        }));
      }
      setCodeEditorTarget(null);
    },
    [activeEditor, codeEditorTarget],
  );

  const handleExport = useCallback(() => {
    if (!model) {
      return;
    }
    exportAnalyticsModelToJson(model);
  }, [model]);

  const handleClone = useCallback(() => {
    if (!model || !currentOrganizationId) {
      return;
    }

    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.Confirm,
        data: {
          title: formatMessage(analyticsModelMessages.cloneModel),
          description: formatMessage(analyticsModelMessages.cloneConfirm),
          okText: formatMessage(analyticsModelMessages.cloneModel),
          cancelText: formatMessage(analyticsModelMessages.cancel),
          onConfirm: async () => {
            dispatch(modalModel.actions.closeModal(ModalID.Confirm));
            try {
              const result = await dispatch(
                analyticsModelModel.cloneAnalyticsModel({
                  id: model.id,
                  targetOrganizationId: currentOrganizationId,
                }),
              ).unwrap();

              await dispatch(
                analyticsModelModel.fetchAnalyticsModels({
                  organization_id: currentOrganizationId,
                }),
              );

              toastCaller({
                type: 'success',
                message: formatMessage(analyticsModelMessages.cloneSuccess),
              });
              onCloned?.(result.local_asset_id);
            } catch (error) {
              toastCaller({
                type: 'error',
                message:
                  error instanceof Error && error.message
                    ? error.message
                    : formatMessage(analyticsModelMessages.errorClone),
              });
            }
          },
        },
      }),
    );
  }, [currentOrganizationId, dispatch, formatMessage, model, onCloned]);

  const handleDelete = useCallback(() => {
    if (!model?.id) {
      return;
    }

    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.Confirm,
        data: {
          title: formatMessage(analyticsModelMessages.deleteConfirm),
          okText: formatMessage(analyticsModelMessages.delete),
          cancelText: formatMessage(analyticsModelMessages.cancel),
          danger: true,
          onConfirm: async () => {
            dispatch(modalModel.actions.closeModal(ModalID.Confirm));
            try {
              await dispatch(
                analyticsModelModel.deleteAnalyticsModel(model.id),
              ).unwrap();
              toastCaller({
                type: 'success',
                message: formatMessage(analyticsModelMessages.deleteSuccess),
              });
              onDeleteComplete?.();
            } catch (error) {
              toastCaller({
                type: 'error',
                message:
                  error instanceof Error && error.message
                    ? error.message
                    : formatMessage(analyticsModelMessages.errorDelete),
              });
            }
          },
        },
      }),
    );
  }, [dispatch, formatMessage, model?.id, onDeleteComplete]);

  const handleRestore = useCallback(async () => {
    if (!model?.id) {
      return;
    }

    try {
      await dispatch(
        analyticsModelModel.restoreAnalyticsModel({ id: model.id }),
      ).unwrap();

      if (currentOrganizationId) {
        await dispatch(
          analyticsModelModel.fetchAnalyticsModels({
            organization_id: currentOrganizationId,
          }),
        );
      }

      toastCaller({
        type: 'success',
        message: formatMessage(analyticsModelMessages.restoreSuccess),
      });
    } catch (error) {
      toastCaller({
        type: 'error',
        message:
          error instanceof Error && error.message
            ? error.message
            : formatMessage(analyticsModelMessages.errorRestore),
      });
    }
  }, [currentOrganizationId, dispatch, formatMessage, model?.id]);

  const isFormValid = draft.name.trim().length > 0;
  const isLanguageLocked = Boolean(model?.id);
  const title = draft.name || formatMessage(analyticsModelMessages.createModel);

  const metaItems = model
    ? [
        {
          label: formatMessage(analyticsModelMessages.modelCreated),
          value: formatDateToYYYYMMDD(new Date(model.created_at)),
        },
      ]
    : [];

  const workspaceNavItems = [
    {
      id: 'details' as const,
      label: formatMessage(analyticsModelMessages.modelDetails),
    },
    {
      id: 'instructions' as const,
      label: formatMessage(analyticsModelMessages.instructions),
    },
    {
      id: 'sections' as const,
      label: formatMessage(analyticsModelMessages.sections),
    },
    {
      id: 'groups' as const,
      label: formatMessage(analyticsModelMessages.groups),
    },
  ];

  return (
    <div className={cn(styles.page, className)}>
      <header className={styles.formHeader}>
        <div className={styles.formHeaderLeft}>
          <span className={styles.eyebrow}>
            {formatMessage(analyticsModelMessages.analyticsModel)}
          </span>
          <div className={styles.titleRow}>
            <h1 className={cn('h3', styles.formTitle)}>{title}</h1>
            {model && model.status !== 'active' && (
              <VersionStatusBadge status={model.status} />
            )}
          </div>
        </div>

        <div className={styles.formHeaderActions}>
          {model?.id && (
            <>
              <Button variant="tertiary" size="medium" onClick={handleExport}>
                {formatMessage(analyticsModelMessages.exportModel)}
              </Button>
              {model.is_system && (
                <Button
                  variant="tertiary"
                  size="medium"
                  onClick={handleClone}
                  disabled={!currentOrganizationId}
                >
                  {formatMessage(analyticsModelMessages.cloneModel)}
                </Button>
              )}
              {model.status === 'deleted' ? (
                <Button
                  variant="tertiary"
                  size="medium"
                  onClick={handleRestore}
                >
                  {formatMessage(analyticsModelMessages.restoreModel)}
                </Button>
              ) : (
                <Button
                  variant="tertiary"
                  size="medium"
                  danger
                  onClick={handleDelete}
                >
                  {formatMessage(analyticsModelMessages.delete)}
                </Button>
              )}
              <span className={styles.headerDivider} aria-hidden />
            </>
          )}
          <Button variant="tertiary" size="medium" onClick={onBackToList}>
            {formatMessage(analyticsModelMessages.cancel)}
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleSave}
            disabled={!isFormValid || hasActiveEditor || !isDirty}
          >
            {formatMessage(analyticsModelMessages.save)}
          </Button>
        </div>
      </header>

      {metaItems.length > 0 && (
        <div className={styles.metaBlock}>
          {metaItems.map((item) => (
            <div key={item.label} className={styles.metaItem}>
              <span className={styles.metaLabel}>{item.label}</span>
              <span className={styles.metaValue}>{item.value}</span>
            </div>
          ))}
        </div>
      )}

      {linkedSchemas.length > 0 && (
        <div className={styles.linkedRow}>
          <span className={styles.linkedLabel}>
            {formatMessage(analyticsModelMessages.linkedEncounterTypes)}
          </span>
          <div className={styles.linkedTags}>
            {linkedSchemas.map((schema) => (
              <Link
                key={schema.id}
                to={buildTemplatesUrl({ specialty: schema.id })}
                className={styles.linkedTag}
              >
                {schema.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      <DndContext
        sensors={dndSensors}
        collisionDetection={closestCenter}
        onDragEnd={handleStructureDragEnd}
      >
        <div className={styles.body}>
          <aside className={styles.sidebar}>
            <NavList>
              {workspaceNavItems.map((item, index) => (
                <NavItem
                  key={item.id}
                  active={activeSidebarTarget === item.id}
                  prefix={`${index + 1}.`}
                  onClick={() => handleSelectWorkspaceBlock(item.id)}
                >
                  {item.label}
                </NavItem>
              ))}
              {draft.groups.length > 0 && (
                <NavList nested>
                  {draft.groups.map((group, groupIndex) => (
                    <NavItem
                      key={group._draftId}
                      nested
                      active={activeSidebarTarget === `group:${group._draftId}`}
                      prefix={`4.${groupIndex + 1}`}
                      onClick={() => handleSelectGroupNavItem(group._draftId)}
                    >
                      {group.name ||
                        formatMessage(analyticsModelMessages.newGroup)}
                    </NavItem>
                  ))}
                </NavList>
              )}
            </NavList>
          </aside>

          <div className={styles.editorPane}>
            <div className={styles.editorToolbar}>
              <div className={styles.editorToolbarGroup}>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={handleAddSection}
                  disabled={hasActiveEditor}
                  icon="plus"
                  className={styles.toolbarButton}
                >
                  {formatMessage(analyticsModelMessages.addSection)}
                </Button>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={handleAddGroup}
                  disabled={hasActiveEditor}
                  icon="plus"
                  className={styles.toolbarButton}
                >
                  {formatMessage(analyticsModelMessages.addGroup)}
                </Button>
              </div>
            </div>

            {hasActiveEditor && (
              <div className={styles.noticeCard}>
                <Icon name="info" size={18} />
                <span>
                  {formatMessage(analyticsModelMessages.activeEditorNotice)}
                </span>
              </div>
            )}

            <div className={styles.editor}>
              <section
                id="analytics-model-details"
                className={styles.editorSection}
                onFocus={() => setActiveSidebarTarget('details')}
              >
                <div className={styles.sectionHeader}>
                  <h2 className={cn('h5', styles.sectionTitle)}>
                    {formatMessage(analyticsModelMessages.modelDetails)}
                  </h2>
                  <Button
                    variant="tertiary"
                    size="small"
                    onClick={() => setCodeEditorTarget({ kind: 'model' })}
                  >
                    {formatMessage(analyticsModelMessages.advanced)}
                  </Button>
                </div>
                <div className={styles.detailsGrid}>
                  <Input
                    name="analyticsModelName"
                    label={formatMessage(analyticsModelMessages.name)}
                    value={draft.name}
                    onChange={(event) =>
                      handleModelNameChange(event.target.value)
                    }
                    required
                    placeholder={formatMessage(
                      analyticsModelMessages.namePlaceholder,
                    )}
                  />
                  <Select
                    label={formatMessage(analyticsModelMessages.language)}
                    options={languageOptions}
                    value={draft.language}
                    onChange={handleLanguageChange}
                    isDisabled={isLanguageLocked}
                    isSearchable={false}
                  />
                </div>
              </section>

              <section
                id="analytics-model-instructions"
                className={styles.editorSection}
                onFocus={() => setActiveSidebarTarget('instructions')}
              >
                <div className={styles.sectionHeader}>
                  <h2 className={cn('h5', styles.sectionTitle)}>
                    {formatMessage(analyticsModelMessages.instructions)}
                  </h2>
                </div>
                <Textarea
                  value={draft.instructions}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      instructions: event.target.value,
                    }))
                  }
                  placeholder={formatMessage(
                    analyticsModelMessages.modelInstructionsPlaceholder,
                  )}
                  rows={6}
                  isElastic
                  className={styles.instructionsArea}
                  textareaClassName={styles.instructionsTextarea}
                />
              </section>

              <section
                id="analytics-model-sections"
                className={styles.editorSection}
                onFocus={() => setActiveSidebarTarget('sections')}
              >
                <div className={styles.sectionHeader}>
                  <h2 className={cn('h5', styles.sectionTitle)}>
                    {formatMessage(analyticsModelMessages.sections)}
                  </h2>
                </div>

                <div className={styles.itemsList}>
                  {draft.sections.length > 0 && (
                    <SortableContext
                      items={draft.sections.map((section) => section._draftId)}
                      strategy={verticalListSortingStrategy}
                    >
                      {draft.sections.map((section, sectionIndex) => (
                        <AnalyticsSectionEditor
                          key={section._draftId}
                          id={section._draftId}
                          section={
                            isSectionEditing(section._draftId) &&
                            activeEditor?.kind === 'section'
                              ? activeEditor.value
                              : section
                          }
                          index={sectionIndex}
                          canEdit
                          isEditing={isSectionEditing(section._draftId)}
                          isInteractionDisabled={
                            hasActiveEditor &&
                            !isSectionEditing(section._draftId)
                          }
                          onChange={handleSectionDraftChange}
                          onEdit={() => handleStartEditingSection(section)}
                          onSave={handleSaveSectionEdit}
                          onCancel={handleCancelSectionEdit}
                          onEditCode={() =>
                            setCodeEditorTarget({
                              kind: 'section',
                              id: section._draftId,
                            })
                          }
                          onRemove={() => handleRemoveSection(section._draftId)}
                        />
                      ))}
                    </SortableContext>
                  )}
                  <Button
                    variant="secondary"
                    size="small"
                    icon="plus"
                    onClick={handleAddSection}
                    disabled={hasActiveEditor}
                    className={styles.addItemButton}
                  >
                    {formatMessage(analyticsModelMessages.addSection)}
                  </Button>
                </div>
              </section>

              <section
                id="analytics-model-groups"
                className={styles.editorSection}
                onFocus={() => setActiveSidebarTarget('groups')}
              >
                <div className={styles.sectionHeader}>
                  <h2 className={cn('h5', styles.sectionTitle)}>
                    {formatMessage(analyticsModelMessages.groups)}
                  </h2>
                </div>

                <div className={styles.itemsList}>
                  {draft.groups.length > 0 && (
                    <SortableContext
                      items={draft.groups.map((group) => group._draftId)}
                      strategy={verticalListSortingStrategy}
                    >
                      {draft.groups.map((group, groupIndex) => (
                        <TagGroupEditor
                          key={group._draftId}
                          id={group._draftId}
                          anchorId={`analytics-model-group-${group._draftId}`}
                          group={
                            isGroupEditing(group._draftId) &&
                            activeEditor?.kind === 'group'
                              ? activeEditor.value
                              : group
                          }
                          index={groupIndex}
                          canEdit
                          isEditing={isGroupEditing(group._draftId)}
                          isInteractionDisabled={
                            hasActiveEditor && !isGroupEditing(group._draftId)
                          }
                          onChange={handleGroupDraftChange}
                          onEdit={() => handleStartEditingGroup(group)}
                          onSave={handleSaveGroupEdit}
                          onCancel={handleCancelGroupEdit}
                          onFocus={() =>
                            setActiveSidebarTarget(`group:${group._draftId}`)
                          }
                          onEditCode={() =>
                            setCodeEditorTarget({
                              kind: 'group',
                              id: group._draftId,
                            })
                          }
                          onRemove={() => handleRemoveGroup(group._draftId)}
                        />
                      ))}
                    </SortableContext>
                  )}
                  <Button
                    variant="secondary"
                    size="small"
                    icon="plus"
                    onClick={handleAddGroup}
                    disabled={hasActiveEditor}
                    className={styles.addItemButton}
                  >
                    {formatMessage(analyticsModelMessages.addGroup)}
                  </Button>
                </div>
              </section>
            </div>
          </div>
        </div>
      </DndContext>

      <AnalyticsModelSettingsPopup
        isOpen={Boolean(codeEditorTarget)}
        code={codeEditorCode}
        onClose={() => setCodeEditorTarget(null)}
        onSave={handleSettingsSave}
      />
    </div>
  );
};
